import classNames from 'classnames';
import { useMemo } from 'react';
import { DealerHours } from 'lib/models/DealerHours';
import { dealerHoursAvailableCss, dealerHoursUnavailableCss } from './styles';

// Helper function to convert time string (e.g., "9:00 am", "8:00 pm") to minutes since midnight
function timeStringToMinutes(timeString: string): number {
  const time = timeString.toLowerCase().trim();
  const [timePart, period] = time.split(' ');

  // eslint-disable-next-line prefer-const
  let [hours, minutes] = timePart.split(':').map(Number);

  if (period === 'pm' && hours !== 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
}

// Helper function to check if current time is between open and close times
function isCurrentlyOpen(openTime: string, closeTime: string): boolean {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = timeStringToMinutes(openTime);
  const closeMinutes = timeStringToMinutes(closeTime);

  // Handle cases where close time is on the next day (e.g., open 9am, close 2am)
  if (closeMinutes < openMinutes) {
    return currentMinutes >= openMinutes || currentMinutes <= closeMinutes;
  }

  return currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
}

function DealerAvailability({ dealerHours }: { dealerHours?: DealerHours }): JSX.Element {
  // Memoize the expensive isOpenNow calculation
  const isOpenNow = useMemo(() => {
    return dealerHours?.todayHours?.open && dealerHours?.todayHours?.close
      ? isCurrentlyOpen(dealerHours.todayHours.open, dealerHours.todayHours.close)
      : false;
  }, [dealerHours?.todayHours?.open, dealerHours?.todayHours?.close]);

  // Memoize class names
  const classNamesMemo = useMemo(
    () => ({
      indicator: classNames(
        'dealer-hours-indicator',
        isOpenNow ? 'open-now-indicator' : 'closed-now-indicator'
      ),
      text: classNames('dealer-hours-text', isOpenNow ? 'open-now-text' : 'closed-now-text'),
    }),
    [isOpenNow]
  );

  // Memoize text content
  const textContent = useMemo(
    () => ({
      status: isOpenNow ? 'Dealership open' : 'Dealership closed',
      time: isOpenNow
        ? `Closes at ${dealerHours?.todayHours?.close}`
        : `Opens at ${dealerHours?.todayHours?.open}`,
    }),
    [isOpenNow, dealerHours?.todayHours?.open, dealerHours?.todayHours?.close]
  );

  const dealerAvailabilityComponent = useMemo(() => {
    function DealerAvailabilityUnavailable(): JSX.Element {
      return (
        <>
          <div className="dealer-hours-container">
            <div className="dealer-hours-indicator" />
            <div className="dealer-hours-text">Dealership hours not available</div>
          </div>
          <style jsx>{dealerHoursUnavailableCss}</style>
        </>
      );
    }

    function DealerAvailabilityAvailable(): JSX.Element {
      return (
        <>
          <div className="dealer-hours-container">
            <div className={classNamesMemo.indicator} />
            <div className={classNamesMemo.text}>{textContent.status}</div>
            <div className="dealer-hours-time">{`- ${textContent.time}`}</div>
          </div>
          <style jsx>{dealerHoursAvailableCss}</style>
        </>
      );
    }

    return dealerHours ? <DealerAvailabilityAvailable /> : <DealerAvailabilityUnavailable />;
  }, [dealerHours, classNamesMemo, textContent]);

  return dealerAvailabilityComponent;
}

export default DealerAvailability;
